import React, { useMemo, useState } from "react";
import { getClassName } from "@utils";
import {
  CheckboxCheckedIcon,
  CheckboxFilledIcon,
  CheckboxIcon,
  ChevronDownIcon,
} from "@taskpane/icons";
import css from "./FilterSelectDropdown.module.scss";

export interface FilterSelectOption {
  label: string;
  value: string;
  isSelected: boolean;
}

export interface FilterSelectDropdownProps {
  id: string;
  label: string;
  options: FilterSelectOption[];
  onChange: (id: string, isSelected: boolean) => void;
  onItemChange: (values: string) => void;
}

const FilterSelectDropdown: React.FC<FilterSelectDropdownProps> = ({
  id,
  label,
  options,
  onChange,
  onItemChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isAllSelected = useMemo(() => options.every(({ isSelected }) => isSelected), [options]);
  const isAnySelected = useMemo(() => options.some(({ isSelected }) => isSelected), [options]);

  const attrs = {
    container: {
      className: getClassName(css.container, isExpanded && css.containerExpanded),
    },
    dropdownButton: {
      type: "button" as const,
      className: css.dropdownButton,
      onClick: () => {
        setIsExpanded((prev) => !prev);
      },
    },
    dropdownLabel: {
      className: css.dropdownLabel,
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(id, !(isAllSelected || isAnySelected));
      },
    },
    chevronIcon: {
      className: css.chevronIcon,
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsExpanded((prev) => !prev);
      },
    },
    optionsWrapper: {
      className: css.optionsWrapper,
    },
  };

  const renderCheckboxIcon = () => {
    if (isAllSelected) {
      return <CheckboxCheckedIcon />;
    }

    if (isAnySelected) {
      return <CheckboxFilledIcon />;
    }

    return <CheckboxIcon />;
  };

  const renderOptions = () =>
    options.map(({ label, value, isSelected }) => {
      const IcomComponent = isSelected ? CheckboxCheckedIcon : CheckboxIcon;
      const attrs = {
        option: {
          type: "button" as const,
          className: css.option,
          onClick: () => {
            onItemChange(value);
          },
        },
      };

      return (
        <button key={value} {...attrs.option}>
          <IcomComponent />
          <span>{label}</span>
        </button>
      );
    });

  return (
    <div {...attrs.container}>
      <button {...attrs.dropdownButton}>
        <span {...attrs.dropdownLabel}>
          {renderCheckboxIcon()}
          <span>{label}</span>
          <ChevronDownIcon {...attrs.chevronIcon} />
        </span>
      </button>
      <div {...attrs.optionsWrapper}>{isExpanded ? renderOptions() : null}</div>
    </div>
  );
};

export default FilterSelectDropdown;
